import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { hreflangLinks } from "@/lib/i18n-urls";

import { SearchForm } from "@/components/booking/SearchForm";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Button } from "@/components/ui/button";
import { getCityPage, getRoutePage, resolveSeoSlug } from "@/lib/catalog.functions";
import { PLACES_BY_SLUG } from "@/lib/local-seo";
import { cityPath, euro, routePath } from "@/lib/pricing";
import { validPrice } from "@/lib/price-display";
import { withMetaCta } from "@/lib/meta-cta";
import { hasSchipholPage } from "@/lib/schiphol-city";
import { airportDepartureGuidance, bookingGuidance } from "@/lib/travel-guidance";

const SITE = "https://taxiritje.nl";

export const Route = createFileRoute("/$slug")({
  loader: async ({ params }) => {
    const resolved = await resolveSeoSlug({ data: { slug: params.slug } });
    if (resolved.type === "route") {
      if (resolved.to === "schiphol" && hasSchipholPage(resolved.from)) {
        throw redirect({ to: "/taxi/$plaats/schiphol", params: { plaats: resolved.from }, statusCode: 301 });
      }
      const page = await getRoutePage({ data: { fromSlug: resolved.from, toSlug: resolved.to } });
      return { kind: "route" as const, slug: params.slug, ...page };
    }
    if (resolved.type === "city") {
      const page = await getCityPage({ data: { slug: resolved.city } });
      if (!page) throw notFound();
      return { kind: "city" as const, slug: params.slug, ...page };
    }
    throw notFound();
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Pagina niet gevonden | Taxiritje.nl" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    if (loaderData.kind === "route") {
      const q = loaderData.quote;
      // Alleen het exacte vaste tarief uit de centrale tarieftabel. Bestaat dat
      // niet, dan noemen wij geen bedrag.
      const shown = q.exact ? q.customerPrice : null;
      const price = shown ? ` ${euro(shown)}` : "";
      const longTitle = `Taxi ${q.fromLabel} naar ${q.toLabel}${price} | Taxiritje.nl`;
      // Laat het "vanaf"-bedrag weg zodra de titel in Google zou worden afgekapt.
      const title =
        longTitle.length <= 62 ? longTitle : `Taxi ${q.fromLabel} naar ${q.toLabel} | Taxiritje.nl`;
      // Begin met het bedrag: dat is het sterkste signaal in de zoekresultaten.
      const description = shown
        ? `Vaste prijs${price} voor een taxi van ${q.fromLabel} naar ${q.toLabel}, ook retour. Direct online boeken, 24/7, bagage en wachttijd inbegrepen.`
        : `Taxi van ${q.fromLabel} naar ${q.toLabel} met vaste prijs vooraf. Direct online boeken, 24/7 beschikbaar, bagage en wachttijd inbegrepen.`;
      return {
        meta: [
          { title },
          { name: "description", content: withMetaCta(description, "nl") },
          { property: "og:title", content: title },
          { property: "og:description", content: withMetaCta(description, "nl") },
          { property: "og:url", content: `${SITE}/${loaderData.slug}` },
          { property: "og:type", content: "website" },
          { name: "twitter:card", content: "summary_large_image" },
        ],
        links: [
          { rel: "canonical", href: `${SITE}/${loaderData.slug}` },
          ...hreflangLinks(`/${loaderData.slug}`),
        ],
      };
    }
    const city = loaderData.city;
    // Bestaat er een uitgebreide lokale pagina (/taxi/<plaats>)? Dan is dat de
    // canonieke versie en voorkomen we twee pagina's met dezelfde zoekintentie.
    const localPage = PLACES_BY_SLUG.get(city.slug);
    // Lange plaatsnamen (bijv. luchthavens) passen niet binnen 62 tekens met de
    // volledige claim; dan korten we de titel automatisch in.
    const longTitle = `Taxi ${city.name} met vaste prijs | Taxiritje.nl`;
    const title = longTitle.length <= 62 ? longTitle : `Taxi ${city.name} | Taxiritje.nl`;
    const description = `Taxi in ${city.name} boeken met vaste prijs. Bekijk de tarieven naar Schiphol, Eindhoven Airport en andere steden en boek direct online.`;
    if (localPage) {
      return {
        meta: [
          { title },
          { name: "description", content: withMetaCta(description, "nl") },
          { name: "robots", content: "noindex, follow" },
          { property: "og:title", content: title },
          { property: "og:description", content: withMetaCta(description, "nl") },
          { property: "og:type", content: "website" },
          { name: "twitter:card", content: "summary_large_image" },
        ],
        links: [
          { rel: "canonical", href: `${SITE}/taxi/${localPage.slug}` },
          ...hreflangLinks(`/taxi/${localPage.slug}`),
        ],
      };
    }
    return {
      meta: [
        { title },
        { name: "description", content: withMetaCta(description, "nl") },
        { property: "og:title", content: title },
        { property: "og:description", content: withMetaCta(description, "nl") },
        { property: "og:url", content: `${SITE}/${loaderData.slug}` },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: `${SITE}/${loaderData.slug}` },
        ...hreflangLinks(`/${loaderData.slug}`),
      ],
    };
  },
  component: SeoPage,
  notFoundComponent: SeoNotFound,
  errorComponent: SeoNotFound,
});

function SeoNotFound() {
  return (
    <SiteLayout>
      <div className="mx-auto max-w-lg px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">Deze pagina bestaat niet</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Bekijk alle routes en prijzen, of boek direct een taxi.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <Button asChild>
            <Link to="/boeken">Taxi boeken</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/prijzen">Alle prijzen</Link>
          </Button>
        </div>
      </div>
    </SiteLayout>
  );
}

function SeoPage() {
  const data = Route.useLoaderData();
  return data.kind === "route" ? <RoutePage data={data} /> : <CityPage data={data} />;
}

type RouteData = Extract<ReturnType<typeof Route.useLoaderData>, { kind: "route" }>;
type CityData = Extract<ReturnType<typeof Route.useLoaderData>, { kind: "city" }>;

function Jsonld({ data }: { data: unknown }) {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}

function Breadcrumbs({ items }: { items: { name: string; url: string }[] }) {
  return (
    <Jsonld
      data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: `${SITE}${item.url}`,
        })),
      }}
    />
  );
}

function RoutePage({ data }: { data: RouteData }) {
  const q = data.quote;
  const faqs = [
    {
      q: `Wat kost een taxi van ${q.fromLabel} naar ${q.toLabel}?`,
      a:
        q.exact && q.customerPrice
          ? `Deze rit kost ${euro(q.customerPrice)} per taxi (1 t/m 4 personen). Dit is een vaste prijs: u betaalt bij het boeken exact dit bedrag. Een minibus, tussenstops of een retourrit rekenen wij er apart bij.`
          : `Vul uw ophaaladres en bestemming in; u ziet direct uw exacte vaste prijs, zonder verrassingen achteraf.`,
    },
    {
      q: "Zit bagage bij de prijs inbegrepen?",
      a: "Ja. Koffers en handbagage zijn inbegrepen. U mag uw eigen kinderzitje meenemen. Geef bij het boeken door hoeveel koffers u meeneemt.",
    },
    {
      q: "Wat gebeurt er als mijn vlucht vertraagd is?",
      a: "Wij volgen uw vluchtnummer. Bij vertraging schuift de ophaaltijd automatisch mee, zonder extra kosten. U krijgt 80 minuten gratis wachttijd na landing.",
    },
    {
      q: "Kan ik betalen na de rit?",
      a: "De boeking is definitief na online betaling. Zakelijke klanten kunnen op factuur rijden; neem daarvoor contact met ons op.",
    },
  ];

  return (
    <SiteLayout>
      <Breadcrumbs
        items={[
          { name: "Home", url: "/" },
          { name: `Taxi ${q.fromLabel}`, url: cityPath(q.fromCity?.slug ?? "") },
          { name: `Taxi ${q.fromLabel} - ${q.toLabel}`, url: `/${data.slug}` },
        ]}
      />
      <Jsonld
        data={{
          "@context": "https://schema.org",
          "@type": "TaxiService",
          name: `Taxi ${q.fromLabel} naar ${q.toLabel}`,
          provider: { "@id": "https://taxiritje.nl/#organization" },
          areaServed: [
            { "@type": "Place", name: q.fromLabel },
            { "@type": "Place", name: q.toLabel },
          ],
          ...(q.exact && q.customerPrice
            ? {
                offers: {
                  "@type": "Offer",
                  price: q.customerPrice,
                  priceCurrency: "EUR",
                  availability: "https://schema.org/InStock",
                  description: `Vaste prijs per taxi van ${q.fromLabel} naar ${q.toLabel}`,
                  priceSpecification: {
                    "@type": "PriceSpecification",
                    priceCurrency: "EUR",
                    price: q.customerPrice,
                    valueAddedTaxIncluded: true,
                    description:
                      "Vaste totaalprijs per taxi, inclusief bagage, tolwegen en parkeerkosten bij luchthavens",
                  },
                },
              }
            : {}),
        }}
      />
      <Jsonld
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />

      <section className="hero-surface">
        <div className="mx-auto max-w-5xl px-4 pb-8 pt-2 sm:pb-11 sm:pt-5">
          <nav className="text-xs text-foreground/60">
            <Link to="/">Home</Link> ·{" "}
            <Link to="/$slug" params={{ slug: cityPath(q.fromCity?.slug ?? "").slice(1) }}>
              Taxi {q.fromLabel}
            </Link>
          </nav>
          <h1 className="mt-2 font-display text-2xl font-bold sm:text-4xl">
            Taxi {q.fromLabel} naar {q.toLabel} met vaste prijs
          </h1>

          <p className="mt-2 max-w-2xl text-sm text-foreground/75">
            {q.exact && q.customerPrice
              ? `Vaste prijs ${euro(q.customerPrice)} per taxi. Direct boeken, 24/7 beschikbaar, bagage en wachttijd inbegrepen.`
              : "Bereken hieronder uw exacte vaste prijs. Direct boeken, 24/7 beschikbaar, bagage en wachttijd inbegrepen."}
          </p>
          {q.exact && q.customerPrice ? (
            <div className="mt-3 inline-flex flex-wrap items-center gap-3 rounded-lg bg-background/95 px-4 py-2 shadow-lg">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Vaste prijs
                </div>
                <div className="font-display text-3xl font-bold text-foreground sm:text-4xl">
                  {euro(q.customerPrice)}
                </div>
                <div className="text-xs text-muted-foreground">per taxi, 1 t/m 4 personen</div>
              </div>
              <Button asChild size="lg">
                <Link to="/boeken">Boeken</Link>
              </Button>
            </div>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-foreground/80">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="size-4 text-primary" /> deur-tot-deur
            </span>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4">
        <div className="float-panel -mt-5 p-3 sm:p-5">
          <SearchForm submitLabel="Boek deze rit" prefill={{ van: q.fromLabel, naar: q.toLabel }} />
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-5">
            <h2 className="font-display text-xl font-bold">
              Taxi {q.fromLabel} – {q.toLabel}: vaste prijs
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              U wordt op het opgegeven ophaaladres in {q.fromLabel} opgehaald en rechtstreeks naar
              uw bestemming in {q.toLabel} gebracht — geen tussenstops, geen medepassagiers. De
              chauffeur houdt rekening met de verkeersdrukte op dit traject en plant de ophaaltijd
              daarop.
              {q.rideType === "airport" &&
                ` ${airportDepartureGuidance("nl")}`}
            </p>
            <h3 className="font-display text-lg font-bold">Wat is inbegrepen</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Vaste prijs per taxi, ongeacht verkeer of wachttijd bij de terminal</li>
              <li>• Bagage, tolwegen en uw eigen kinderzitje meenemen</li>
              <li>• 80 minuten gratis wachttijd na landing, 15 minuten bij stadsritten</li>
              <li>• Erkende chauffeur met taxipas en nette, verzekerde auto</li>
            </ul>

            <h2 className="mt-8 font-display text-xl font-bold">
              Deze rit vooraf reserveren of retour boeken
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              U kunt deze rit vooraf reserveren voor 1 t/m 8 personen; bij het reserveren geeft u uw
              ophaaladres met huisnummer en uw bestemming op. Wilt u ook terug, dan kiest u retour
              en plant u de terugrit in dezelfde boeking. {bookingGuidance("nl")}
            </p>

            {q.rideType === "airport" && /(airport|schiphol|luchthaven)/i.test(q.toLabel) ? (
              <>
                <h2 className="mt-8 font-display text-xl font-bold">
                  Terugrit van {q.toLabel} naar {q.fromLabel}
                </h2>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  De terugrit van {q.toLabel} naar {q.fromLabel} kost hetzelfde
                  {q.exact && q.customerPrice ? ` bedrag: ${euro(q.customerPrice)} per taxi` : ""}.
                  U geeft uw vluchtnummer op; wij volgen de vlucht en schuiven de ophaaltijd mee bij
                  vertraging. Na landing heeft u 80 minuten gratis wachttijd, ruim genoeg voor
                  bagage en douane. De chauffeur wacht bij Vertrekhal 3, uitgang C, en neemt na
                  landing telefonisch of per sms contact met u op. Ook de terugrit reserveert u minimaal drie uur
                  vooraf.
                </p>
              </>
            ) : null}

            <h2 className="mt-8 font-display text-xl font-bold">Veelgestelde vragen</h2>

            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.q} className="card-elevated p-4">
                  <div className="font-semibold">{faq.q}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="space-y-5">
            <div className="card-elevated p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Vaste prijs
              </div>
              <div className="mt-1 font-display text-3xl font-bold">
                {q.exact && q.customerPrice ? euro(q.customerPrice) : "Bereken uw vaste prijs"}
              </div>
              <Button asChild className="mt-4 w-full font-semibold">
                <Link
                  to="/boeken"
                  search={{ van: q.fromCity?.slug ?? "", naar: q.toCity?.slug ?? "" }}
                >
                  Nu boeken <ArrowRight className="ml-1 size-4" />
                </Link>
              </Button>
            </div>

            {q.fromCity?.slug && q.toCity?.slug && (
              <div className="card-elevated p-5">
                <div className="font-display font-bold">Ook de andere richting</div>
                <ul className="mt-3 space-y-2 text-sm">
                  <li>
                    <Link
                      to="/$slug"
                      params={{ slug: routePath(q.toCity.slug, q.fromCity.slug).slice(1) }}
                      className="hover:underline"
                    >
                      Taxi {q.toLabel} → {q.fromLabel}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/$slug"
                      params={{ slug: cityPath(q.fromCity.slug).slice(1) }}
                      className="hover:underline"
                    >
                      Taxi {q.fromLabel}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/$slug"
                      params={{ slug: cityPath(q.toCity.slug).slice(1) }}
                      className="hover:underline"
                    >
                      Taxi {q.toLabel}
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            {data.related.length > 0 && (
              <div className="card-elevated p-5">
                <div className="font-display font-bold">Andere ritten vanuit {q.fromLabel}</div>
                <ul className="mt-3 space-y-2 text-sm">
                  {data.related
                    .filter((item: { price: number }) => validPrice(item.price) !== null)
                    .map((item: { slug: string; name: string; price: number }) => (
                      <li key={item.slug} className="flex items-center justify-between gap-2">
                        <Link
                          to="/$slug"
                          params={{ slug: routePath(q.fromCity?.slug ?? "", item.slug).slice(1) }}
                          className="hover:underline"
                        >
                          {q.fromLabel} → {item.name}
                        </Link>
                        <span className="font-semibold">{euro(item.price)}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}

function CityPage({ data }: { data: CityData }) {
  const { city, destinations, nearbyCities = [] } = data;
  return (
    <SiteLayout>
      <Breadcrumbs
        items={[
          { name: "Home", url: "/" },
          { name: `Taxi ${city.name}`, url: `/${data.slug}` },
        ]}
      />
      <Jsonld
        data={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `Taxiritje.nl ${city.name}`,
          telephone: "+31850607086",
          areaServed: city.name,
          priceRange: "€€",
        }}
      />

      <section className="hero-surface">
        <div className="mx-auto max-w-5xl px-4 pb-8 pt-2 sm:pb-11 sm:pt-5">
          <h1 className="font-display text-2xl font-bold sm:text-4xl">Taxi {city.name}</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground/75">
            Taxi in {city.name} met vaste prijs. Kies uw bestemming, zie direct het tarief en boek
            online — luchthavenvervoer, zakelijke ritten en stadsritten.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4">
        <div className="float-panel -mt-5 p-3 sm:p-5">
          <SearchForm submitLabel="Bekijk prijs" prefill={{ van: city.name }} />
        </div>

        <h2 className="mt-10 font-display text-xl font-bold">
          Populaire bestemmingen vanuit {city.name} met vaste prijs
        </h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {destinations
            .filter((destination: { price: number }) => validPrice(destination.price) !== null)
            .map((destination: { slug: string; name: string; price: number }) => (
              <Link
                key={destination.slug}
                to="/$slug"
                params={{ slug: routePath(city.slug, destination.slug).slice(1) }}
                className="card-elevated flex items-center justify-between gap-3 p-4 transition-shadow hover:shadow-lg"
              >
                <div>
                  <div className="text-sm font-semibold">
                    Taxi {city.name} → {destination.name}
                  </div>
                  <div className="text-xs text-muted-foreground">vaste prijs, direct boeken</div>
                </div>
                <span className="font-display text-xl font-bold">{euro(destination.price)}</span>
              </Link>
            ))}
        </div>

        <div className="card-elevated mt-10 space-y-3 p-6">
          <h2 className="font-display text-xl font-bold">Taxi in {city.name} boeken</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Onze chauffeurs rijden dagelijks in {city.name}
            {city.province ? ` en de rest van ${city.province}` : ""}. U boekt online met een vaste
            prijs, dus u weet vooraf precies wat de rit kost. Voor luchthavenvervoer plannen wij de
            ophaaltijd op basis van uw vluchtnummer en de actuele verkeersdrukte.
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Bij het reserveren geeft u uw ophaaladres met huisnummer en uw bestemming op. Kiest u
            retour, dan plant u de terugrit in dezelfde boeking. Wij rijden 1 t/m 8 personen en uw
            bagage rijdt gratis mee.
          </p>
        </div>

        <div className="mt-10">
          <h2 className="font-display text-xl font-bold">
            Veelgestelde vragen over taxi {city.name}
          </h2>
          <dl className="mt-4 space-y-4">
            <div className="card-elevated p-4">
              <dt className="font-semibold">Wat kost een taxi in {city.name}?</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                U ziet de vaste prijs per rit direct bij uw ophaaladres en bestemming. De prijs
                geldt per taxi voor 1 t/m 4 personen; voor 5 t/m 8 personen rijdt een taxibus.
              </dd>
            </div>
            <div className="card-elevated p-4">
              <dt className="font-semibold">Is de prijs echt vast?</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                Ja. Het bedrag dat u bij het boeken ziet is de prijs die u betaalt, ongeacht drukte,
                omleidingen of de route die de chauffeur kiest.
              </dd>
            </div>
            <div className="card-elevated p-4">
              <dt className="font-semibold">Hoe ver vooraf moet ik reserveren?</dt>
              <dd className="mt-1 text-sm text-muted-foreground">
                Reserveren kan tot drie uur voor het ophalen. Wilt u ook terug, kies dan retour bij
                het boeken; u kunt uw rit tot 24 uur voor vertrek zelf wijzigen.
              </dd>
            </div>
          </dl>
        </div>

        {nearbyCities.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-xl font-bold">Plaatsen in de buurt van {city.name}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Wij rijden ook vanuit deze plaatsen; de afstand is de rijafstand vanaf {city.name}.
            </p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {nearbyCities.map((c: { name: string; slug: string; km: number }) => (
                <li key={c.slug}>
                  <a href={cityPath(c.slug)} className="font-medium hover:underline">
                    {c.name}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="card-elevated mt-10 mb-4 p-6 text-center">
          <h2 className="font-display text-xl font-bold">Taxi {city.name} boeken</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Vul uw ophaaladres en bestemming in en zie direct de vaste prijs.
          </p>
          <Button asChild className="mt-4">
            <Link to="/boeken">Taxi boeken met vaste prijs</Link>
          </Button>
        </div>
      </div>
    </SiteLayout>
  );
}
